import { useDebouncedCallback } from "@ui/hooks/use-debounced-callback";
import { useCallback, useEffect, useRef, useState } from "react";

export type DebouncedSearchState = "idle" | "loading" | "results" | "empty";

export interface UseDebouncedSearchOptions {
  /** Queries shorter than this reset to idle without fetching. */
  minLength?: number;
  delayMs?: number;
  /** When false the hook neither fetches nor schedules (e.g. modal closed). */
  enabled?: boolean;
}

export interface UseDebouncedSearchReturn<T> {
  query: string;
  setQuery: (query: string) => void;
  /** Set the input text without triggering a search — for adopting a picked result's label. */
  setQuerySilently: (query: string) => void;
  results: T[];
  state: DebouncedSearchState;
  reset: () => void;
}

/**
 * Debounced JSON search against a `?q=` endpoint with stale-response protection:
 * each keystroke aborts the in-flight request, so results can never arrive out of
 * order. Shared by the admin Mapbox and stock-photo pickers.
 */
export function useDebouncedSearch<T>(
  searchPath: string,
  { minLength = 2, delayMs = 250, enabled = true }: UseDebouncedSearchOptions = {},
): UseDebouncedSearchReturn<T> {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<T[]>([]);
  const [state, setState] = useState<DebouncedSearchState>("idle");
  const abortRef = useRef<AbortController>(undefined);
  const silentValueRef = useRef<string | null>(null);

  const fetchResults = useCallback(
    async (q: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setState("loading");
      try {
        const response = await fetch(`${searchPath}?q=${encodeURIComponent(q)}`, {
          headers: { Accept: "application/json" },
          signal: controller.signal,
        });
        if (response.ok) {
          const data: T[] = await response.json();
          setResults(data);
          setState(data.length > 0 ? "results" : "empty");
        } else {
          // HTTP error statuses resolve rather than throw — without this the
          // hook would sit in "loading" until the next keystroke.
          setState("empty");
        }
      } catch (e) {
        if (e instanceof DOMException && e.name === "AbortError") return;
        setState("empty");
      }
    },
    [searchPath],
  );

  const debouncedFetch = useDebouncedCallback(fetchResults, delayMs);

  useEffect(() => {
    if (!enabled) return;
    if (silentValueRef.current !== null) {
      // Value-keyed rather than a boolean: if the silently-set value never
      // re-runs this effect (it equaled the current query), the marker must
      // not swallow the next genuinely typed query.
      const wasSilent = silentValueRef.current === query;
      silentValueRef.current = null;
      if (wasSilent) return;
    }
    if (query.length < minLength) {
      debouncedFetch.cancel();
      setResults([]);
      setState("idle");
      return;
    }

    debouncedFetch(query);

    return () => {
      debouncedFetch.cancel();
      abortRef.current?.abort();
    };
  }, [query, debouncedFetch, enabled, minLength]);

  const setQuerySilently = useCallback((q: string) => {
    silentValueRef.current = q;
    setQuery(q);
  }, []);

  const reset = useCallback(() => {
    debouncedFetch.cancel();
    setQuery("");
    setResults([]);
    setState("idle");
    abortRef.current?.abort();
  }, [debouncedFetch]);

  return { query, setQuery, setQuerySilently, results, state, reset };
}
